import { useAtom } from '@reatom/npm-react'
import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import {
  reatomFollowingsFilter,
  UserConnectionsFollowings,
} from '@root/features/user/user-connections'
import { reatomUsersSearch } from '@root/features/user/user-connections/atoms/reatom-users-search'
import * as FollowingsSearchMode from '@root/features/user/users-search/model/followings-search-mode'
import { UsersSearch } from '@root/features/user/users-search/ui/users-search'
import { WhoToFollowList } from '@root/features/user/who-to-follow/ui/who-to-follow-list'
import { GlobalSearchUsersResults } from '@root/pages/profile/board/user-connections-board/global-search-users-results'

import type { DelugeId } from '@root/entities/user-short'

type UserConnectionsBoardFollowingProps = {
  delugeId: DelugeId
}

export const UserConnectionsBoardFollowing = ({
  delugeId,
}: UserConnectionsBoardFollowingProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [searchMode, setSearchMode] =
    useState<FollowingsSearchMode.FollowingsSearchMode>('local')

  const followingFilter = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomFollowingsFilter(delugeId),
    [delugeId],
  )

  const globalModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomUsersSearch({ debounce: 300, startSearchSymbols: 3 }),
    [],
  )
  const [searchQuery, setLocalSearchQuery] = useAtom(
    followingFilter.searchQueryAtom,
  )
  const [, setUsersSearchQuery] = useAtom(globalModel.searchQueryAtom)

  const setSearchQuery = (newSearchQuery: string) => {
    setLocalSearchQuery(newSearchQuery)
    setUsersSearchQuery(newSearchQuery)
  }

  return (
    <Root>
      <Content ref={ref}>
        <Head>
          <UsersSearch
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchMode={searchMode}
            setSearchMode={setSearchMode}
            ref={ref}
          />
        </Head>
        {FollowingsSearchMode.match(searchMode, {
          onGlobal: () => <GlobalSearchUsersResults model={globalModel} />,
          onLocal: () => (
            <UserConnectionsFollowings followingsFilter={followingFilter} />
          ),
        })}
      </Content>
      <WhoToFollow delugeId={delugeId} />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  padding: 10px;

  &[data-empty='true'] {
    padding: 6px;
  }
`

const Head = styled.div`
  z-index: 1;
  position: sticky;
  top: 0;
  padding: 12px 0;
  background: var(--background-1);
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 0 16px 12px 6px;
  overflow: auto;
`

const WhoToFollow = styled(WhoToFollowList)`
  margin-top: -40px;
  width: 300px;
`
